import { getNavigation, getPartners, NavigationProps } from '@amf/shared/utils/navigation';
import { MatchDetailItemFragment } from '@amf/shared/types/graphql';
import { GetServerSideProps } from 'next';
import { getLanguageLocale } from '@amf/shared/utils/languages';
import { sdk } from '@amf/shared/lib/graphQlClient';
import MatchHeaderAlt from '@amf/shared/components/match/MatchHeaderAlt';
import MatchEvents from '@amf/shared/components/match/MatchEvents';
import PlayerNews from '@amf/shared/components/player/PlayerNews';
import { useTranslate } from '@tolgee/react';
import Container from '@amf/shared/components/layout/Container';
import MatchRosterList from '@amf/shared/components/match/MatchRosterList';
import Content from '@amf/shared/components/content/Content';
import MatchPreviousList from '@amf/shared/components/match/MatchPreviousList';
import TeamComparison from '@amf/shared/components/team/TeamComparison';
import MatchContumacy from '@amf/shared/components/match/MatchContumacy';
import { MatchContumacy as MatchContumacyType } from '@amf/shared/data/Match';
import useMatch from '@amf/shared/components/match/hooks/useMatch';
import { useMemo } from 'react';
import MatchJumbotron from '@amf/shared/components/match/MatchJumbotron';
import { useRouter } from 'next/router';
import { OgImageType, ogImageUrl } from '@amf/shared/components/og/OgImage';

import Layout from 'components/layout/Layout';
import Meta from 'components/Meta';
import { AppLinks } from 'utils/links';

interface MatchDetailPageProps extends NavigationProps {
  match: MatchDetailItemFragment;
}

export default function MatchDetailPage({
  navigation,
  partners,
  match: standard,
}: MatchDetailPageProps) {
  const { t } = useTranslate();
  const { locale } = useRouter();
  const { match: live } = useMatch(standard.id, standard.live);
  const match = useMemo<MatchDetailItemFragment>(() => {
    if (live) return live;
    return standard;
  }, [live, standard]);

  return (
    <Layout navigation={navigation} partners={partners}>
      <Meta
        title={`${match.home?.team.name ?? '-'} vs ${match.visiting?.team.name ?? '-'}`}
        image={ogImageUrl(locale, OgImageType.matchDetail, { id: match.id })}
      />
      <MatchHeaderAlt
        match={match}
        prefixes={{
          team: AppLinks.team,
          matches: AppLinks.matches,
          ground: AppLinks.ground,
          referee: AppLinks.referee,
        }}
      />
      {!match.contumacy && match.home && match.visiting && <MatchJumbotron match={match} />}
      {match.content && (
        <Container>
          <Content content={match.content} />
        </Container>
      )}
      {match.contumacy && (
        <Container>
          <MatchContumacy type={match.contumacy as MatchContumacyType} />
        </Container>
      )}
      {!match.contumacy && (
        <Container>
          <MatchRosterList
            match={match}
            prefixes={{ player: AppLinks.player, team: AppLinks.team }}
            title={t('match.roster.title')}
            center
          />
        </Container>
      )}
      {!match.contumacy && match.home && match.visiting && (
        <MatchEvents
          match={match}
          homeTeamId={match.home.team.id}
          prefixes={{ player: AppLinks.player, team: AppLinks.team, offense: AppLinks.offenses }}
        />
      )}
      <Container>
        {match.home && match.visiting && (
          <TeamComparison
            center
            title={t('match.comparison.title')}
            leagueId={match.leagueId}
            firstTeamId={match.home.team.id}
            secondTeamId={match.visiting.team.id}
            prefixes={{ team: AppLinks.team }}
          />
        )}
        <MatchPreviousList
          match={match}
          prefixes={{ match: AppLinks.match }}
          title={t('match.previous.title')}
          center
        />
      </Container>
      {match.news.length > 0 && (
        <Container>
          <PlayerNews
            title={t('match.news')}
            news={match.news}
            prefixes={{ news: AppLinks.newsList }}
          />
        </Container>
      )}
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps<MatchDetailPageProps, { id: string }> = async ({
  locale,
  params,
  res,
}) => {
  const { id } = params;
  const { match } = await sdk.MatchDetail({ union: process.env.NEXT_PUBLIC_UNION, id });

  if (!match) {
    return {
      notFound: true,
    };
  }

  res.setHeader('Cache-Control', 'max-age=0, s-maxage=3600');
  return {
    props: {
      match,
      navigation: await getNavigation(getLanguageLocale(locale!)),
      partners: await getPartners(),
    },
  };
};
